import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../../common/ConfirmModal';
import DriverTransactionRowActions from '../transactions/DriverTransactionRowActions';

const { rootUrl } = require('../../../../config/config');

const DriverTransactions = ({ driverId, token: propToken }) => {
    const navigate = useNavigate();
    const authToken = propToken || localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    const getHeaders = () => authToken ? { Authorization: `Bearer ${authToken}` } : {};

    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [selectedDeleteTransaction, setSelectedDeleteTransaction] = useState(null);

    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

    const [selectedType, setSelectedType] = useState('');
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [paymentTypesMap, setPaymentTypesMap] = useState({});

    // build options from paymentTypesMap when available, otherwise use derived transactionTypes
    const paymentOptions = Object.keys(paymentTypesMap).length > 0
        ? Object.values(paymentTypesMap).map(p => ({ value: p.type, label: p.short_desc || p.type })).sort((a, b) => a.label.localeCompare(b.label))
        : transactionTypes;

    const [showNotesModal, setShowNotesModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const canDeleteTransactions = permissions.includes('roles.manage') || permissions.includes('drivers.delete');

    // Limit and ordering are fixed for driver transactions UI

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // fetch canonical driver payment types to get human-friendly labels
        (async () => {
            try {
                const resp = await axios.get(`${rootUrl}/payments/driver-types`, { headers: getHeaders() });
                const list = Array.isArray(resp.data?.data) ? resp.data.data : [];
                const map = {};
                list.forEach(p => {
                    // use `type` as key; short_desc is the friendly label
                    if (p && p.type) map[p.type] = p;
                });
                setPaymentTypesMap(map);
            } catch (e) {
                setPaymentTypesMap({});
            }
        })();

        fetchTransactions();
    }, [authToken, driverId]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        (async () => {
            if (!authToken) {
                setPermissions([]);
                return;
            }
            try {
                const resp = await axios.get(`${rootUrl}/api/users/me/permissions`, { headers: getHeaders() });
                setPermissions(resp.data?.data || []);
            } catch (err) {
                setPermissions([]);
            }
        })();
    }, [authToken]);

    const buildUrl = (overrides = {}) => {
        const params = new URLSearchParams();
        const df = overrides.date_from || dateFrom;
        const dt = overrides.date_to || dateTo;
        if (!df || !dt) return null;
        params.append('date_from', df);
        params.append('date_to', dt);
        if (overrides.type || selectedType) params.append('type', overrides.type || selectedType);
        // enforce fixed paging and ordering for driver transactions
        params.append('limit', overrides.limit || 200);
        if (overrides.offset) params.append('offset', overrides.offset);
        params.append('order_by', overrides.order_by || 'date');
        params.append('order', overrides.order || 'asc');

        return `${rootUrl}/drivers/${encodeURIComponent(driverId)}/transactions?` + params.toString();
    };

    const fetchTransactions = async (overrides = {}) => {
        setLoading(true);
        setError(null);
        try {
            const url = buildUrl(overrides);
            if (!url) throw new Error('Please provide both date_from and date_to');
            const res = await axios.get(url, { headers: getHeaders() });

            // Handle new structured response format
            const rows = res.data?.data || res.data || [];
            setTransactions(rows);

            // derive types and sort alphabetically for a friendlier dropdown
            const unique = Array.from(new Set(rows.map(r => r.type).filter(Boolean)));
            const types = unique
                .map(t => ({ value: t, label: (paymentTypesMap[t]?.short_desc) || t }))
                .sort((a, b) => a.label.localeCompare(b.label));
            setTransactionTypes(types);
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message || 'Failed to load transactions';
            setError(errorMsg);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    const formatMoney = (value) => {
        const v = parseFloat(value || 0);
        return v === 0 ? '-' : v.toLocaleString();
    };

    const formatDate = (iso) => {
        if (!iso) return 'N/A';
        try { const d = new Date(iso); if (isNaN(d)) return iso; return d.toLocaleDateString(); } catch (e) { return iso; }
    };

    const applyFilters = () => fetchTransactions({});

    const clearFilters = () => {
        setSelectedType('');
        const d = new Date();
        d.setDate(d.getDate() - 30);
        setDateFrom(d.toISOString().split('T')[0]);
        setDateTo(new Date().toISOString().split('T')[0]);
        // keep limit/order defaults (hardcoded in request)
        setTimeout(fetchTransactions, 0);
    };

    const handleViewNotes = (transaction) => {
        setSelectedTransaction(transaction);
        setShowNotesModal(true);
    };

    const handleViewTransaction = (transaction) => {
        navigate(`/drivers/transactions/${transaction.type}/${transaction.doc_no}`);
    };

    const handleDeleteRequest = (transaction) => {
        setDeleteError('');
        setSelectedDeleteTransaction(transaction);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedDeleteTransaction) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await axios.delete(
                `${rootUrl}/drivers/transactions/${encodeURIComponent(selectedDeleteTransaction.driver_id)}/${encodeURIComponent(selectedDeleteTransaction.type)}/${encodeURIComponent(selectedDeleteTransaction.doc_no)}`,
                {
                    headers: getHeaders(),
                    data: { reason: null }
                }
            );
            setDeleteModalOpen(false);
            setSelectedDeleteTransaction(null);
            await fetchTransactions();
        } catch (err) {
            setDeleteError(err.response?.data?.message || err.message || 'Failed to delete transaction');
        } finally {
            setDeleteLoading(false);
        }
    };

    const filtered = transactions.filter(t => {
        if (selectedType && t.type !== selectedType) return false;
        return true;
    });

    return (
        <div className="text-sm">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[#2a2e30]">Driver Transactions</h3>

            </div>

            <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h4 className="text-base font-semibold text-[#2a2e30] mb-4">Filter Options</h4>
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-[150px]">
                        <label className="block text-xs font-semibold text-[#2a2e30] mb-2">Date From</label>
                        <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-xl" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </div>
                    <div className="flex-1 min-w-[150px]">
                        <label className="block text-xs font-semibold text-[#2a2e30] mb-2">Date To</label>
                        <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-xl" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                    <div className="flex-1 min-w-[100px]">
                        <label className="block text-xs font-semibold text-[#2a2e30] mb-2">Transaction Type</label>
                        <Select
                            isClearable
                            value={paymentOptions.find(o => o.value === selectedType) || null}
                            onChange={(opt) => setSelectedType(opt ? opt.value : '')}
                            options={paymentOptions}
                            placeholder="All Types"
                            className="w-full"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={applyFilters} className="px-6 py-2 bg-[#FF5733] text-white rounded-xl hover:bg-[#e64d2e] transition-colors">Apply</button>
                        <button onClick={clearFilters} className="px-6 py-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Clear</button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-12"><FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-[#FF5733]" /></div>
            ) : (
                <div>
                    {filtered.length > 0 ? (
                        <div className="overflow-x-auto bg-white rounded-2xl shadow-xl p-6">
                            <table className="min-w-full">
                                <thead>
                                        <tr className="border-b border-gray-200">
                                            <th className="text-left py-3 px-4 font-semibold text-[#2a2e30] text-sm">Date</th>
                                            <th className="text-left py-3 px-4 font-semibold text-[#2a2e30] text-sm">Doc No</th>
                                            <th className="text-left py-3 px-4 font-semibold text-[#2a2e30] text-sm">Type</th>
                                            <th className="text-left py-3 px-4 font-semibold text-[#2a2e30] text-sm">In</th>
                                            <th className="text-left py-3 px-4 font-semibold text-[#2a2e30] text-sm">Out</th>
                                            <th className="text-left py-3 px-4 font-semibold text-[#2a2e30] text-sm">Post Balance</th>
                                            <th className="text-left py-3 px-4 font-semibold text-[#2a2e30] text-sm">Input By</th>
                                            <th className="text-left py-3 px-4 font-semibold text-[#2a2e30] text-sm">Notes</th>
                                            <th className="text-left py-3 px-4 font-semibold text-[#2a2e30] text-sm">Actions</th>
                                        </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((trans) => (
                                        <tr key={trans.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                                            <td className="py-3 px-4 text-[#2a2e30] text-sm">{formatDate(trans.date)}</td>
                                            <td className="py-3 px-4 text-[#2a2e30] text-sm"><span className="font-medium">{trans.doc_no}</span></td>
                                            <td className="py-3 px-4 text-[#2a2e30] text-sm">{paymentTypesMap[trans.type]?.short_desc || trans.type}</td>
                                            <td className="py-3 px-4 text-right font-mono text-green-600 text-sm">{formatMoney(trans.credit)}</td>
                                            <td className="py-3 px-4 text-right font-mono text-red-600 text-sm">{formatMoney(trans.debit)}</td>
                                            <td className="py-3 px-4 text-right font-mono text-[#2a2e30] text-sm">{formatMoney(trans.post_balance)}</td>
                                            <td className="py-3 px-4 text-[#2a2e30] text-sm">{trans.input_by}</td>
                                            <td className="py-3 px-4 text-sm">{trans.notes && (<button onClick={() => handleViewNotes(trans)} className="text-[#FF5733]">View</button>)}</td>
                                            <td className="py-3 px-4 text-[#2a2e30]">
                                                <DriverTransactionRowActions
                                                    transaction={trans}
                                                    canDelete={canDeleteTransactions && !trans.parent_trans_id}
                                                    onView={handleViewTransaction}
                                                    onDelete={handleDeleteRequest}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-gray-50 rounded-lg"> <p className="text-gray-500">No transactions found for the selected criteria.</p></div>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={deleteModalOpen}
                title="Delete Transaction"
                description={selectedDeleteTransaction ? `Delete transaction ${selectedDeleteTransaction.doc_no} for driver ${selectedDeleteTransaction.driver_id}? This will archive it.` : ''}
                onCancel={() => {
                    if (deleteLoading) return;
                    setDeleteModalOpen(false);
                    setSelectedDeleteTransaction(null);
                    setDeleteError('');
                }}
                onConfirm={handleDeleteConfirm}
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleteLoading}
                error={deleteError}
                variant="danger"
            />

            {showNotesModal && selectedTransaction && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-[#2a2e30]">Transaction Notes</h3>
                            <button onClick={() => setShowNotesModal(false)} className="text-gray-500"><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="space-y-4">
                            <div><label className="block text-sm font-semibold text-[#2a2e30]">Transaction ID:</label><p className="font-mono bg-gray-100 p-3 rounded-xl">{selectedTransaction.id}</p></div>
                            <div><label className="block text-sm font-semibold text-[#2a2e30]">Notes:</label><div className="bg-gray-100 p-4 rounded-xl"><p className="whitespace-pre-wrap">{selectedTransaction.notes || 'No notes available'}</p></div></div>
                        </div>
                        <div className="flex justify-end mt-6"><button onClick={() => setShowNotesModal(false)} className="px-6 py-2 bg-gray-100 rounded-xl">Close</button></div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DriverTransactions;
